// Convert Customer Journey Map to Visual Story Planner format

export const journeyToStoryData = (journeyData) => {
  const { persona, features, story_arc, stages, metadata, problems, product_statement } = journeyData;
  
  // Generate story title
  const storyTitle = metadata?.project_name || 'Customer Journey Story';
  
  // Get safe arrays with defaults
  const safeProblems = problems || [];
  const safeFeatures = features || [];
  const safeStages = stages || [];
  
  // Get main problem statement (first problem)
  const mainProblem = safeProblems.length > 0 && safeProblems[0].statement 
    ? safeProblems[0].statement 
    : 'Customer faces challenges';
  
  // Create start node
  const startNode = {
    id: 'start-node',
    title: 'Meet the Hero',
    content: `${persona?.name || 'Our Customer'}, ${persona?.role || 'the user'}\n\n${mainProblem}`,
    x: 100,
    y: 100
  };
  
  // Create problem nodes
  const problemNodes = safeProblems
    .filter(p => p.statement)
    .map((problem, idx) => ({
      id: `problem-${idx}`,
      title: `Problem ${idx + 1}`,
      content: `${problem.statement}\n\nCost: ${problem.cost_amount || 'Not specified'}\nMarket: ${problem.tam_amount || 'Not specified'}`,
      x: 100 + (idx * 250),
      y: 200
    }));
  
  // Create solution nodes from problem-specific solutions
  const solutionNodes = [];
  safeProblems.forEach((problem, pIdx) => {
    if (problem.solutions && problem.solutions.length > 0) {
      problem.solutions.filter(s => s.name).forEach((solution, sIdx) => {
        solutionNodes.push({
          id: `solution-${pIdx}-${sIdx}`,
          title: solution.name,
          content: solution.description || 'Solves the problem',
          problemId: pIdx,
          x: 100 + (pIdx * 250) + (sIdx * 150),
          y: 350
        });
      });
    }
  });
  
  // Create feature nodes
  const featureNodes = safeFeatures
    .filter(f => f.name && f.value)
    .map((feature, idx) => ({
      id: `feature-${idx}`,
      title: feature.name,
      content: `Value: ${feature.value}\n\nEvidence: ${feature.evidence || 'To be validated'}`,
      x: 100 + (idx * 250),
      y: 450
    }));
  
  // Create journey stage nodes
  const stageNodes = safeStages
    .filter(s => s.action || s.thoughts)
    .map((stage, idx) => ({
      id: `stage-${idx}`,
      title: stage.name,
      content: `Action: ${stage.action || 'Not specified'}\nThoughts: ${stage.thoughts || 'Not specified'}\nFeeling: ${stage.feeling || 'Not specified'}`,
      x: 100 + (idx * 200),
      y: 600
    }));
  
  // Create success node
  const successNode = {
    id: 'success-node',
    title: 'Success',
    content: story_arc?.success || 'Customer achieves their goal',
    x: 100 + (safeStages.length * 200),
    y: 750
  };
  
  // Combine all nodes
  const nodes = [
    startNode,
    ...problemNodes,
    ...solutionNodes,
    ...featureNodes,
    ...stageNodes,
    successNode
  ];
  
  // Create connections
  const connections = [];
  
  // Connect start to problems
  problemNodes.forEach(pn => {
    connections.push({
      id: `conn-start-${pn.id}`,
      source: startNode.id,
      target: pn.id,
      label: 'identifies'
    });
  });
  
  // Connect problems to their solutions
  solutionNodes.forEach(sn => {
    connections.push({
      id: `conn-problem-${sn.id}`,
      source: `problem-${sn.problemId}`,
      target: sn.id,
      label: 'solved by'
    });
  });
  
  // Connect features to stages
  if (featureNodes.length > 0 && stageNodes.length > 0) {
    connections.push({
      id: 'conn-features-stages',
      source: featureNodes[0].id,
      target: stageNodes[0].id,
      label: 'begins journey'
    });
  }
  
  // Connect stages sequentially
  for (let i = 0; i < stageNodes.length - 1; i++) {
    connections.push({
      id: `conn-stage-${i}`,
      source: stageNodes[i].id,
      target: stageNodes[i + 1].id,
      label: 'then'
    });
  }
  
  // Connect last stage to success
  if (stageNodes.length > 0) {
    connections.push({
      id: 'conn-stages-success',
      source: stageNodes[stageNodes.length - 1].id,
      target: successNode.id,
      label: 'achieves'
    });
  }
  
  return {
    story_title: storyTitle,
    nodes,
    connections,
    metadata: {
      generated_from: 'Customer Journey Wizard',
      generated_at: new Date().toISOString(),
      source_project: metadata?.project_name || 'Unknown'
    }
  };
};

export const journeyToTweeCode = (journeyData) => {
  const { persona, features, story_arc, stages, metadata, problems, product_statement } = journeyData;
  
  // Safe arrays
  const safeProblems = problems || [];
  const safeFeatures = features || [];
  const safeStages = stages || [];
  
  // Get main problem
  const mainProblem = safeProblems.length > 0 && safeProblems[0].statement 
    ? safeProblems[0].statement 
    : 'Our customer faces challenges';
  
  let twee = `:: StoryTitle\n${metadata?.project_name || 'Customer Journey'}\n\n`;
  
  // Start passage
  twee += `:: Start\n`;
  twee += `**${persona?.name || 'Our Customer'}** - ${persona?.role || 'the user'}\n\n`;
  twee += `${mainProblem}\n\n`;
  
  // Link to problems or features
  if (safeProblems.length > 0 && safeProblems[0].statement) {
    twee += `[[Explore the problems|Problems]]\n\n`;
  } else {
    twee += `[[Learn about the solution|Features]]\n\n`;
  }
  
  // Problems passage (if problems exist)
  if (safeProblems.some(p => p.statement)) {
    twee += `:: Problems\n`;
    twee += `**The Challenges We Face**\n\n`;
    safeProblems.filter(p => p.statement).forEach((p, idx) => {
      twee += `**Problem ${idx + 1}**: ${p.statement}\n`;
      if (p.cost_amount) {
        twee += `_Cost: ${p.cost_amount}_\n`;
      }
      if (p.market_opportunity) {
        twee += `_Opportunity: ${p.market_opportunity}_\n`;
      }
      twee += `\n`;
    });
    twee += `[[See our solutions|Solutions]]\n\n`;
    
    // Solutions passage
    twee += `:: Solutions\n`;
    twee += `**How We Solve These Problems**\n\n`;
    if (product_statement) {
      twee += `${product_statement}\n\n`;
    }
    safeProblems.forEach((problem, pIdx) => {
      if (problem.solutions && problem.solutions.length > 0) {
        twee += `**For Problem ${pIdx + 1}:**\n`;
        problem.solutions.filter(s => s.name).forEach(s => {
          twee += `- **${s.name}**: ${s.description || 'Addresses the issue'}\n`;
        });
        twee += `\n`;
      }
    });
    twee += `[[Explore key features|Features]]\n\n`;
  }
  
  // Features passage
  twee += `:: Features\n`;
  twee += `**Key Product Features**\n\n`;
  if (safeFeatures.filter(f => f.name).length === 0) {
    twee += `Our solution offers comprehensive features to help you succeed.\n\n`;
  } else {
    safeFeatures.filter(f => f.name).forEach(f => {
      twee += `**${f.name}**: ${f.value || 'Adds value'}\n`;
      if (f.evidence) {
        twee += `_Evidence: ${f.evidence}_\n\n`;
      }
    });
  }
  twee += `[[Begin the journey|${safeStages[0]?.name || 'Journey'}]]\n\n`;
  
  // Stage passages
  safeStages.forEach((stage, idx) => {
    if (!stage.action && !stage.thoughts) return;
    
    twee += `:: ${stage.name}\n`;
    twee += `**${stage.name} Stage**\n\n`;
    if (stage.action) twee += `Action: ${stage.action}\n\n`;
    if (stage.thoughts) twee += `Thoughts: ${stage.thoughts}\n\n`;
    if (stage.feeling) twee += `Feeling: ${stage.feeling}\n\n`;
    
    const nextStage = safeStages[idx + 1];
    if (nextStage && (nextStage.action || nextStage.thoughts)) {
      twee += `[[Continue to ${nextStage.name}|${nextStage.name}]]\n`;
    } else {
      twee += `[[See the outcome|Success]]\n`;
    }
    twee += `\n`;
  });
  
  // Success passage
  twee += `:: Success\n`;
  twee += `**Journey Complete!**\n\n`;
  twee += `${story_arc?.success || 'The customer achieves their goal.'}\n\n`;
  twee += `[[Start over|Start]]\n`;
  
  return twee;
};
